/**
 * Web implementation of the shared-core PlatformCapabilities port.
 *
 * Vendored copy of apps/frontend/src/platform/capabilities.ts — see
 * ../app/components/primitives/README.md for why the two shells duplicate
 * rather than share their web-only plumbing.
 *
 * This is the only file in apps/customer-portal outside components/primitives/
 * that is allowed to touch browser globals directly. The portability check
 * (scripts/check_frontend_portability.py) enforces this.
 */
import { passkeyCapabilities } from "@williampollardco/portal-ui";
import type {
  GlobalKeyEvent,
  PlatformCapabilities,
} from "@shared-core/capabilities/ports";

/** Hand a viewer tab an object URL for already-fetched bytes.
 *
 * The blob carries its own media type, so the browser picks its viewer from
 * that — which is why the binary path must never round-trip through a string
 * (see `openBlobInNewTab` on the port). */
function openObjectUrlInNewTab(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  // The opened tab references the object URL; revoke after a grace period so
  // it isn't leaked but the tab has had time to load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// Nested sheets share one lock: the body is released when the last one closes.
let scrollLocks = 0;
let scrollBeforeLock = "";
/** The icon index.html set, kept the first time a theme replaces it so a later null can put it back. */
let shellFavicon: { href: string; type: string | null } | null = null;

export const webCapabilities: PlatformCapabilities = {
  ...passkeyCapabilities,

  // Handover between the two portals: a top-level form POST to the other origin, so the token travels in a body.
  submitTopLevelForm(action: string, fields: Record<string, string>) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = action;
    form.style.display = "none";
    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
  },

  openExternalUrl(url: string) {
    window.open(url, "_blank");
  },

  printCurrentPage() {
    window.print();
  },

  async copyToClipboard(text: string) {
    await navigator.clipboard.writeText(text);
  },

  async readClipboard(): Promise<string> {
    return navigator.clipboard.readText();
  },

  // This shell has no file field to paste a screenshot into.
  pastedImage(): Blob | null {
    return null;
  },

  getShareOrigin(): string {
    return window.location.origin;
  },

  redirectTo(url: string) {
    window.location.href = url;
  },

  registerGlobalKeydownListener(handler: (event: GlobalKeyEvent) => void) {
    const listener = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      handler({
        key: event.key,
        hasModifier: event.metaKey || event.ctrlKey || event.altKey,
        fromTextEntry: Boolean(
          target &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.tagName === "SELECT" ||
            target.isContentEditable),
        ),
        preventDefault: () => event.preventDefault(),
      });
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  },

  getCurrentPath(): string {
    return window.location.pathname;
  },

  downloadTextFile(
    filename: string,
    content: string,
    mimeType: string = "text/plain",
  ) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  },

  downloadBlob(filename: string, blob: Blob) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  },

  openContentInNewTab(content: string, mimeType: string) {
    openObjectUrlInNewTab(new Blob([content], { type: mimeType }));
  },

  openBlobInNewTab(blob: Blob) {
    openObjectUrlInNewTab(blob);
  },

  setVisibilityAwareInterval(callback: () => void, ms: number): () => void {
    const tick = () => {
      if (!document.hidden) {
        callback();
      }
    };
    const id = window.setInterval(tick, ms);
    return () => window.clearInterval(id);
  },

  getViewportSize() {
    return { width: window.innerWidth, height: window.innerHeight };
  },

  getStorageItem(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      // Storage access can throw under strict cookie/privacy modes.
      // Fail open: caller treats null as "no preference set".
      return null;
    }
  },

  setStorageItem(key: string, value: string | null): void {
    try {
      if (value === null) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, value);
      }
    } catch {
      // Same rationale: failing to persist a UI preference is non-fatal.
    }
  },

  getSessionItem(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      // Same rationale as getStorageItem: fail open, the caller treats null as "nothing kept".
      return null;
    }
  },

  setSessionItem(key: string, value: string | null): void {
    try {
      if (value === null) {
        window.sessionStorage.removeItem(key);
      } else {
        window.sessionStorage.setItem(key, value);
      }
    } catch {
      // Same rationale as setStorageItem: failing to keep a UI choice is non-fatal.
    }
  },

  getDeviceCredential(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  setDeviceCredential(key: string, value: string | null): void {
    try {
      if (value === null) {
        window.localStorage.removeItem(key);
      } else {
        window.localStorage.setItem(key, value);
      }
    } catch {
      // Same rationale as setStorageItem: a failed write degrades to
      // "terminal not set up" on next boot rather than crashing the kiosk.
    }
  },

  warnBeforeUnload(active: boolean): () => void {
    if (!active) return () => {};
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  },

  matchesMediaQuery(query: string): boolean {
    return window.matchMedia(query).matches;
  },

  onMediaQueryChange(query: string, handler: () => void) {
    const list = window.matchMedia(query);
    list.addEventListener("change", handler);
    return () => list.removeEventListener("change", handler);
  },

  // The portal's sheets do not sit above an on-screen keyboard.
  getKeyboardInset: () => 0,

  onKeyboardInsetChange: () => () => {},

  // The customer's portal theme names the tab and gives it their icon; with no theme the page keeps index.html's.
  setDocumentBranding({ title, faviconUrl }) {
    document.title = title;
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!icon) return;
    shellFavicon ??= {
      href: icon.getAttribute("href") ?? "",
      type: icon.getAttribute("type"),
    };
    if (faviconUrl) {
      icon.setAttribute("href", faviconUrl);
      // The theme's icon may be a PNG or an ICO: let the browser sniff it rather than claim SVG.
      icon.removeAttribute("type");
    } else {
      icon.setAttribute("href", shellFavicon.href);
      if (shellFavicon.type) icon.setAttribute("type", shellFavicon.type);
    }
  },

  lockPageScroll() {
    scrollLocks += 1;
    if (scrollLocks === 1) {
      scrollBeforeLock = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    let isReleased = false;
    return () => {
      if (isReleased) return;
      isReleased = true;
      scrollLocks -= 1;
      if (scrollLocks === 0) document.body.style.overflow = scrollBeforeLock;
    };
  },
};
